import { combineLatest, debounceTime, distinctUntilChanged, Observable, Subject, switchMap, tap } from 'rxjs'
import {
   HistoricalProductData,
   NewCodeRequest,
   Practice,
   PracticesClient,
   PricingManagerClient,
   PricingPimsProductSearchResult,
   VCProductSearchResult,
} from 'src/app/shared/services/ppv.api'
import { Component, effect, input, model, signal, output, inject, computed } from '@angular/core'
import { VetcorPricingRowService } from '../vetcor-pricing-table/vetcor-pricing-row.service'

@Component({
   selector: 'vetcor-pricing-new-code-admin',
   standalone: false,
   templateUrl: './vetcor-pricing-new-code-admin.component.html',
   styleUrl: './vetcor-pricing-new-code-admin.component.scss',
})
export class VetcorPricingNewCodeAdminComponent {
   practicesApi = inject(PracticesClient)
   pricingRowService = inject(VetcorPricingRowService)

   public onClose = output()
   public newCodeRequestId = input<number>(0)
   public practiceSKey = signal<number>(0)
   public firstHm = computed(() => (this.practice()?.hMs?.length > 0 ? this.practice()?.hMs[0] : null))
   public practice = signal<Practice>(undefined)
   public editVcProduct: boolean = false
   public oldCost = model<number>(undefined)
   public newCost = model<number>(undefined)
   public newPrice = model<number>(undefined)
   public finalComment = model<string>(undefined)
   readonly = input<boolean>(false)

   public pricehistory = signal<any[]>(undefined)

   public isSaving = signal<boolean>(false)

   public historicalProductData = signal<HistoricalProductData>(undefined)

   isSearching: boolean = false
   public searchResult$: Observable<VCProductSearchResult[]>
   public searchText$ = new Subject<string>()
   public searchResults: Array<any> = []

   public financialImpact = signal<
      | Array<{
           label: string
           value: number
           revenueImpact?: number
           marginImpact?: number
        }>
      | undefined
   >(undefined)

   public editSimilarCode: boolean = false
   public similarSearchResults = signal([])
   public similarCode = model<PricingPimsProductSearchResult | undefined>(undefined)

   public searchVcProduct$ = new Subject<string>()
   public searchProduct$ = new Subject<string>()

   public vcProduct = model<VCProductSearchResult>(undefined)

   public requestData: NewCodeRequest = {} as any

   constructor(private pricingManagerClient: PricingManagerClient) {
      effect(() => {
         const id = this.newCodeRequestId()

         console.log('newCodeRequestId', id)
         if (id === undefined || id === 0) {
            return
         }

         console.log('fetching', id)
         this.pricingManagerClient.getNewCodeRequest(id).subscribe({
            next: (data) => {
               this.requestData = data

               this.finalComment.set(data.finalComment)
               this.newPrice.set(data.productPrice)
               this.oldCost.set(data.oldCostOverride)
               this.newCost.set(data.newCostOverride)
               this.practiceSKey.set(data.practiceSKey)
               if (this.requestData?.vcProductSKey) {
                  this.pricingManagerClient.searchVCProducts(undefined, data.practiceSKey, this.requestData.vcProductSKey, undefined).subscribe({
                     next: (data) => {
                        this.vcProduct.set(data[0])
                     },
                  })
               }

               if (!!this.requestData.similarProductSKey) {
                  this.pricingManagerClient
                     .searchProduct(this.requestData?.practiceSKey, undefined, this.requestData.similarProductSKey, undefined)
                     .pipe(tap((data) => this.processPimsProducts([data])))
                     .pipe(tap((newProduct) => this.similarCode.set(newProduct[0])))
                     .subscribe()
               }
            },
         })
      })

      effect(() => {
         const practiceSKey = this.practiceSKey()
         if (practiceSKey === undefined || practiceSKey === 0) {
            return
         } else {
            this.practicesApi.getPractice(practiceSKey).subscribe({
               next: (data) => {
                  this.practice.set(data)
               },
            })
         }
      })

      effect(() => {
         const selectedProduct = this.vcProduct()
         const selectedPimsProduct = this.similarCode()

         console.log({ selectedProduct, selectedPimsProduct })

         if (!selectedProduct?.vcProductSKey) {
			if (this.newPrice()) {
			   const newCodePrice = this.newPrice() ?? undefined
			   const newPrices = []
			   newPrices.push({ label: 'Manual', value: this.newPrice() })
               this.financialImpact.set(newPrices)
			} else {
               this.financialImpact.set(undefined)
			}
         } else {
            const oldCodePrice = this.similarCode()?.price ?? undefined
            const newCodePrice = this.newPrice() ?? undefined
            const newCodeCost = !this.newCost() ? this.requestData.newCost ?? undefined : this.newCost()
            const oldCodeCost = !this.oldCost() ? this.requestData.oldCost ?? undefined : this.oldCost()
            const oldCodeMargin = oldCodePrice - oldCodeCost
            const marginNeutralPrice = newCodeCost + oldCodePrice - oldCodeCost
            const nationalIndexPrice = this.vcProduct()?.nationalIndexPrice ?? undefined
            const stateIndexPrice = this.vcProduct()?.stateIndexPrice ?? undefined
            const currentCompetitorPrice = this.vcProduct()?.competitorPrice ?? undefined
            const l12MQty = this.similarCode()?.l12MQty

            const newPrices = []

            newPrices.push({ label: 'Revenue Neutral', value: oldCodePrice })
            newPrices.push({ label: 'Margin Neutral', value: marginNeutralPrice })
            newPrices.push({ label: 'National Index', value: nationalIndexPrice })
            newPrices.push({ label: 'State Index', value: stateIndexPrice })
            newPrices.push({ label: 'Competitor', value: currentCompetitorPrice })
            newPrices.push({ label: 'Manual', value: this.newPrice() })

            newPrices.forEach((price) => {
               if (!!price.value) {
                  price.revenueImpact = l12MQty * (price.value - oldCodePrice)
                  price.marginImpact = l12MQty * (price.value - newCodeCost - oldCodeMargin)
               }
            })

            this.financialImpact.set(newPrices)
         }
      })

      effect(() => {
         const selectedPimsProduct = this.similarCode()
         if (!selectedPimsProduct?.pimsId) {
            return
         }

         this.pricingManagerClient
            .getPriceChangeHistory({ practiceSKey: selectedPimsProduct.practiceSKey, pimsId: selectedPimsProduct.pimsId, recordType: 'P' } as any)
            .subscribe({
               next: (data) => {
                  this.pricehistory.set(data)
                  console.log(this.pricehistory())
               },
            })

         this.pricingManagerClient.getHistoricalProductData(selectedPimsProduct.practiceSKey, selectedPimsProduct.pimsId).subscribe({
            next: (data) => {
               this.historicalProductData.set(data)
            },
         })
      })
   }

   ngOnInit(): void {
      this.searchVcProduct$
         .pipe(
            debounceTime(500),
            distinctUntilChanged(),
            tap((term) => {
               console.log(term)
               this.isSearching = true
            }),
            switchMap((term: string) =>
               this.pricingManagerClient.searchVCProducts(term, this.practiceSKey(), undefined, undefined).pipe(
                  tap(() => {
                     this.isSearching = false
                  })
               )
            )
         )
         .subscribe({
            next: (data) => {
               console.log(data)
               this.searchResults = data
            },
         })
   }

   getPriceValue(price: number) {
      if (price == undefined || isNaN(price)) {
         return undefined
      }

      return price
   }

   onCodeSearch(term: string) {
      this.searchVcProduct$.next(term)
   }

   onSimilarCodeSearch(term: string) {
      this.pricingManagerClient
         .searchProduct(this.requestData?.practiceSKey, term, undefined, undefined)
         .pipe(tap((data) => this.processPimsProducts(data)))
         .pipe(tap((data) => this.similarSearchResults.set(data)))
         .subscribe()
   }

   processPimsProducts(data: any) {
      data.forEach((product) => {
         data.displayName = this.getPimsProductLabel(product)
      })
   }

   getPimsProductLabel(product: any) {
      let newName = product.productName

      if (!!product.code) newName = '(' + product.code + ') - ' + newName

      return newName
   }

   setSearchResultsVM(results: any) {
      this.similarSearchResults.set(results)
   }

   startZohoFeedback() {
      this.isSaving.set(true)
      const ncr = {
         id: this.requestData.id,
         vcProductSKey: this.requestData.vcProductSKey,
         similarProductSKey: this.requestData.similarProductSKey,
         oldCostOverride: this.oldCost(),
         newCostOverride: this.newCost(),
         productPrice: this.newPrice(),
         finalComment: this.finalComment(),
      } as NewCodeRequest
      this.pricingManagerClient.createZohoTicketForNewCodeRequest(ncr).subscribe({
         next: (data) => {
            this.requestData.zohoTicketId = data
         },
         complete: () => {
            this.isSaving.set(false)
         },
      })
   }

   openTicket() {
      window.open('https://servicedesk.vetcor.com/agent/vetcorllc/pricing/tickets/details/' + this.requestData.zohoTicketId, '_blank')
   }

   saveWithPrice(price: number, isApproved: boolean) {
      this.isSaving.set(true)
      const ncr = {
         id: this.requestData.id,
         practiceSKey: this.requestData.practiceSKey,
         newProductCode: this.requestData.newProductCode,
         vcProductSKey: this.vcProduct()?.vcProductSKey,
         similarProductSKey: this.similarCode()?.productSKey,
         oldCostOverride: this.oldCost(),
         newCostOverride: this.newCost(),
         productPrice: price,
         finalComment: this.finalComment(),
         isApproved: isApproved,
      } as NewCodeRequest

      this.pricingRowService.saveNewCodeRequest(ncr, () => {
         this.onClose?.emit()
         this.isSaving.set(false)
      })
   }

   isStaleDate(date: string | Date | null | undefined): boolean {
      if (!date) return false
      const scrapeTime = new Date(date).getTime()
      const nowMinus3Days = new Date().getTime() - 3 * 86400000
      return scrapeTime < nowMinus3Days
   }
}
